/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react'

const validTerms = [5, 10, 15, 20, 25, 50]

function formatCurrency(value) {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  return currencyFormatter.format(value).replace('.00', '')
}

function calculateAmounts(initialAmount, rates) {
  // Calculations taken from https://www.cfbmc.org/powerful-giving/planned-giving/projected-return-calculator
  const amounts = []

  const ANNUAL_RATE_OF_RETURN = rates.growth
  const ANNUAL_SPENDING_RATE = rates.gift
  const FEE_PERCENTAGE = rates.fee

  const ror = ANNUAL_RATE_OF_RETURN * 0.01
  const sr = ANNUAL_SPENDING_RATE * 0.01
  const feePer = FEE_PERCENTAGE * 0.01

  const mv = new Array(51)
  const fee = new Array(51)
  const spending = new Array(51)
  const grants = new Array(51)
  const cumlgrants = new Array(51)

  mv[0] = initialAmount
  fee[0] = mv[0] * feePer
  spending[0] = mv[0] * sr
  grants[0] = 0
  cumlgrants[0] = grants[0]

  let previousyear = 0
  let marketvalue = 0

  for (var i = 1; i <= 51; ++i) {
    previousyear = mv[i - 1]
    marketvalue = previousyear * (1 + ror)

    fee[i] = previousyear * feePer
    spending[i] = previousyear * sr
    mv[i] = marketvalue - fee[i] - spending[i]

    grants[i] = spending[i]
    cumlgrants[i] = grants[i] + cumlgrants[i - 1]

    if (validTerms.indexOf(i) !== -1) {
      amounts.push({
        term: `${i} Years`,
        marketValue: Math.round(mv[i]),
        fee: Math.round(fee[i]),
        grants: Math.round(grants[i]),
        total: Math.round(cumlgrants[i]),
      })
    }
  }

  return amounts
}

function getInitialState() {
  const state = []

  for (let i = 1; i <= 51; ++i) {
    if (validTerms.indexOf(i) !== -1) {
      state.push({
        term: `${i} Years`,
        marketValue: '',
        fee: '',
        grants: '',
        total: '',
      })
    }
  }

  return state
}

function App({ rates }) {
  const [value, setValue] = useState('')
  const [amounts, setAmounts] = useState(getInitialState())

  const handleChange = (e) => {
    let cleanValue = e.target.value

    cleanValue = cleanValue.replace('$', '')

    setValue(cleanValue)
  }

  useEffect(() => {
    let initialAmount = parseFloat(value.split(',').join(''))

    if (isNaN(initialAmount)) {
      initialAmount = ''
    }

    const newAmounts = calculateAmounts(initialAmount, rates)

    setAmounts(newAmounts)
  }, [value])

  return (
    <>
      <div className="py-8">
        <form className="max-w-sm mx-auto">
          <div className="relative py-2 border-b-2 border-gray-400">
            <div className="absolute top-0 left-0 flex w-8 h-full" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 17" className="m-auto h-1/2">
                <path
                  d="M6.216 16.344v-2.168c.71-.117 1.265-.283 1.665-.498.4-.215.75-.495 1.05-.84.605-.69.908-1.475.908-2.354s-.307-1.655-.923-2.329c-.615-.674-1.515-1.186-2.7-1.538V2.662c1.178.274 2.175.827 2.988 1.66V2.076C8.508 1.49 7.512 1.1 6.216.904V.182h-1.64v.722c-1.127.157-2.032.559-2.715 1.206C1.177 2.758.835 3.541.835 4.46c0 .469.127.933.381 1.392.254.459.604.85 1.05 1.176.446.326 1.216.677 2.31 1.055v4.297a6.467 6.467 0 01-1.949-.713c-.582-.332-1.186-.781-1.811-1.348v2.461c1.4.827 2.653 1.293 3.76 1.397v2.168h1.64zM4.576 6.1a3.29 3.29 0 00-.245-.108c-.58-.24-.983-.485-1.21-.732-.228-.248-.342-.56-.342-.938 0-.416.167-.78.503-1.089.335-.309.766-.5 1.293-.57V6.1zm1.64 6.26V8.735l.303.166c.52.274.88.536 1.079.786.198.251.298.569.298.953 0 .885-.56 1.458-1.68 1.718z"
                  fill="#2E2D2D"
                  fillRule="nonzero"
                />
              </svg>
            </div>

            <input
              type="number"
              value={value}
              onChange={handleChange}
              placeholder="Enter an Initial Gift"
              className="w-full pl-10 font-normal lg:text-lg xl:text-xl replace-focus"
            />
          </div>
        </form>
      </div>

      <div className="sm-down:overflow-x-scroll overflow-scroll-touch">
        <table className="w-full md:mt-4">
          <thead>
            <tr className="text-center text-white bg-brand-blue">
              <th className="p-4 lg:px-6 xl:px-16">Term</th>
              <th className="p-4 leading-tight">
                <div>Total Market Value</div>
                <div className="italic font-light">({rates.growth}% Growth Rate)</div>
              </th>
              <th className="p-4 leading-tight">
                <div>Annual Admin. Fees</div>
                <div className="italic font-light">({rates.fee}% Fee Rate)</div>
              </th>
              <th className="p-4 leading-tight">
                <div>Annual Grants</div>
                <div className="italic font-light">({rates.gift}% Gift Rate)</div>
              </th>
              <th className="p-4 leading-tight">Total Grants Awarded</th>
            </tr>
          </thead>

          <tbody className="border-b border-gray-400 divide-y divide-gray-400">
            {amounts.map((row) => (
              <tr key={row.term} className="divide-x divide-gray-400">
                <td className="p-4 font-semibold text-center text-brand-blue">{row.term}</td>
                <td className="p-4">
                  <div className="px-4 py-2 border border-gray-300 rounded xl:px-5 xl:py-3">
                    {formatCurrency(row.marketValue)}
                  </div>
                </td>
                <td className="p-4">
                  <div className="px-4 py-2 border border-gray-300 rounded xl:px-5 xl:py-3">
                    {formatCurrency(row.fee)}
                  </div>
                </td>
                <td className="p-4">
                  <div className="px-4 py-2 border border-gray-300 rounded xl:px-5 xl:py-3">
                    {formatCurrency(row.grants)}
                  </div>
                </td>
                <td className="p-4">
                  <div className="px-4 py-2 border border-gray-300 rounded xl:px-5 xl:py-3">
                    {formatCurrency(row.total)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 text-sm text-center text-gray-500">(for illustration purposes only)</div>
    </>
  )
}

export default App
